<template>
  <component :is="tag" :class="classes">
    <component
      :is="emphasizedDate ? 'button' : 'div'"
      class="asd20-card__content"
      :class="emphasizedDate ? 'asd20-card__event-content' : ''"
    >
      <div class="asd20-card__header">
        <div class="asd20-card__emphasized-date" v-if="emphasizedDate">
          <div class="top">{{ weekday }}</div>
          <div class="middle">{{ day }}</div>
          <div class="bottom">{{ month }}</div>
          <small>{{ year }}</small>
        </div>
        <component :is="headerTag" v-if="title" class="asd20-card__title">
          <span v-html="title"></span>
          <div
            class="asd20-card__subtitle"
            v-if="subtitle"
            v-html="subtitle"
          ></div>
        </component>
      </div>

      <p class="asd20-card__date" v-if="date && !emphasizedDate">
        <asd20-icon name="calendar" size="sm" />
        <span v-if="modifiedDate && showModifiedDate" class="date-meta">
          <span>Updated: </span>
          <span
            v-if="modifiedDate && showModifiedDate"
            v-html="modifiedDate"
          ></span>
        </span>
        <span v-else class="date-meta">
          <span>Published: </span>
          <span v-if="date && !emphasizedDate" v-html="date"></span>
        </span>
      </p>
      <p v-if="time" class="asd20-card__time">
        <asd20-icon name="hours-alt" size="sm" />
        <span v-html="time"></span>
      </p>

      <p v-if="location" class="asd20-card__location">
        <asd20-icon name="map" size="sm" />
        <span v-html="location"></span>
      </p>

      <p class="asd20-card__description">
        <span
          v-if="!expandedDescription && truncatedDescription"
          v-html="truncatedDescription"
        ></span>
        <span
          v-if="expandedDescription && description"
          v-html="description"
        ></span>
        <a
          class="asd20-card__link"
          v-if="link"
          :href="link ? link : ''"
          :title="title"
          v-html="linkLabel"
          :tabindex="disableFocus ? -1 : 0"
        ></a>
      </p>
      <div v-if="showMeta" class="asd20-card__meta">
        <div v-if="categories" class="asd20-card__categories">
          <asd20-tag
            v-for="category in categories"
            :key="category"
            :label="category"
            transparent
          ></asd20-tag>
        </div>
        <div
          class="asd20-card__indicators"
          v-if="hasFile || hasGallery || hasVideo || sourceOrg"
        >
          <asd20-icon v-if="hasVideo" name="video" size="sm" />
          <asd20-icon v-if="hasGallery" name="gallery" size="sm" />
          <asd20-icon v-if="hasFile" name="file" size="sm" />
          <img
            v-if="sourceOrg"
            width="24px"
            :src="websiteLogo"
            :alt="`${sourceOrg} logo`"
          />
        </div>
      </div>
      <slot />
    </component>
    <figure class="asd20-card__image" v-if="image && !noCoverImage">
      <img v-lazy="image" :key="image" :alt="alt || ''" />
    </figure>
    <asd20-icon
      v-if="pinned"
      class="asd20-card__pinned"
      name="pin-filled"
      size="sm"
    />
    <slot name="bottom" />
  </component>
</template>

<script>
import Asd20Icon from '../../atoms/Asd20Icon'
import Asd20Tag from '../../atoms/Asd20Tag'
import truncate from 'lodash-es/truncate'

export default {
  name: 'Asd20Card',

  components: { Asd20Icon, Asd20Tag },

  props: {
    tag: { type: String, default: 'div' },
    headerTag: { type: String, default: 'h3' },
    image: { type: String, default: '' },
    alt: { type: String, default: '' },
    title: { type: String, default: '' },
    subtitle: { type: String, default: '' },
    categories: { type: Array, default: () => [] },
    description: {
      type: String,
      default: '',
    },
    link: { type: String, default: '' },
    linkLabel: { type: String, default: '' },
    emphasizedDate: { type: Boolean, default: false },
    date: { type: String, default: '' },
    modifiedDate: { type: String, default: '' },
    time: { type: String, default: '' },
    weekday: { type: String, default: '' },
    day: { type: String, default: '' },
    month: { type: String, default: '' },
    year: { type: String, default: '' },
    location: { type: String, default: '' },
    selected: { type: Boolean, default: false },
    reversed: { type: Boolean, default: false },
    pinned: { type: Boolean, default: false },
    zoom: { type: Boolean, default: false },
    seperateImage: { type: Boolean, default: false },
    expandedHeader: { type: Boolean, default: false },
    expandedDescription: { type: Boolean, default: false },
    hasGallery: { type: Boolean, default: false },
    hasVideo: { type: Boolean, default: false },
    hasFile: { type: Boolean, default: false },
    disableFocus: { type: Boolean, default: false },
    noCoverImage: { type: Boolean, default: false },
    sourceOrg: { type: String, default: '' },
    websiteLogo: { type: String, default: '' },
  },
  computed: {
    truncatedDescription() {
      const strippedDescription = this.description
        //remove anchor tags but leave anchor text description
        .replace(/<a[^>]*>(.*?)<\/a>/g, '$1')
        // remove other html tags
        // .replace(/<[^>]+>/g, '')
      return truncate(strippedDescription, { length: 150, separator: /,? +/ })
    },
    classes() {
      return {
        'asd20-card': true,
        'asd20-card--without-image': !this.image || this.noCoverImage,
        'asd20-card--with-image': !!this.image && !this.noCoverImage,
        'asd20-card--selected': this.selected,
        'asd20-card--reversed': this.reversed,
        'asd20-card--zoom': this.zoom,
        'asd20-card--seperate-image': this.seperateImage,
        'asd20-card--pinned': this.pinned,
        'asd20-card--emphasized-date': this.emphasizedDate,
        'asd20-card--expanded-header': this.expandedHeader,
        'asd20-card--expanded-description': this.expandedDescription,
      }
    },
    showMeta() {
      return (
        (this.categories && this.categories.length > 0) ||
        this.hasVideo ||
        this.hasGallery ||
        this.hasFile
      )
    },
    showModifiedDate() {
      const publishDateTimeString = new Date(this.date)
      const modifiedDateTimeString = new Date(this.modifiedDate)
      // console.log(publishDateTimeString, modifiedDateTimeString)

      let publishDateTimePlusOne = new Date(
        publishDateTimeString.setDate(publishDateTimeString.getDate() + 1)
      )
      // console.log(publishDateTimePlusOne, modifiedDateTimeString)
      return modifiedDateTimeString > publishDateTimePlusOne ? true : false
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../../design/_variables.scss';
@import '../../../design/_mixins.scss';

.asd20-card {
  position: relative;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  z-index: 1;
  min-height: 40vw;
  backface-visibility: hidden;

  border-radius: var(--website-shape__radius-m);
  border-color: var(--website-card__border-color);
  overflow: hidden;
  transition: transform 0.3s ease-out, box-shadow 0.3s ease-out;
  border: none;
  text-decoration: none;

  box-shadow: 0 0 0 var(--website-shape__line-thickness)
      var(--website-card__border-color) inset,
    var(--website-card__shadow);
  background-color: var(--website-page__background-color);

  background: var(--website-card__background-color)
    var(--website-card__background-style);
  color: var(--website-card__foreground-color);
  transform: var(--website-card__transform);

  font-family: var(--website-typography__font-family-body);
  line-height: 1.2;
  font-size: 1rem;

  // ::v-deep .asd20-icon g {
  //   --line-color: var(--color__accent);
  //   --line-color: red;
  // }
  &__content {
    z-index: 2;
    padding: space(1);
    margin: 0 0 0 0;
    flex-shrink: 0;
    flex-grow: 0;
    display: flex;
    flex-direction: column;
  }
  &__event-content {
    background: transparent;
    border: none;
    text-align: left;
    align-items: inherit;
    color: inherit;
  }

  &__title {
    font-family: var(--website-typography__font-family-headlines);
    font-weight: var(--website-typography__font-weight-headlines);
    line-height: 1.2;
    // font-size: 1.25em;
    color: var(--website-page__title-color);
    margin-top: 0;
  }

  &__subtitle {
    font-family: var(--website-typography__font-family-headlines);
    font-weight: var(--website-typography__font-weight-body);
    line-height: 1.2;
    font-size: 0.75em;
    color: var(--website-page__subtitle-color);
  }

  &__description {
    // display: none;
    font-size: 0.875rem !important;
    line-height: $line-height;
    margin-bottom: 0 !important;
    span {
      display: block;
      margin-top: space(0.5);
      &::v-deep li {
        margin-left: 1rem;
        font-size: 0.875rem !important;
      }
    }
  }

  &__link {
    font-size: 0.875rem;
    color: var(--website-page__link-color) !important;
    text-decoration: underline;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      background-color: transparent;
      z-index: 2;
      border-radius: var(--website-shape__radius-m);
    }
    &:focus::after {
      box-shadow: 0 0 0 2px var(--color__accent-s30) inset,
        0 0 0 4px white inset;
      // @include asd20-focus;
    }
    &:active::after {
      box-shadow: none;
    }
  }

  &--selected {
    .asd20-card__link:focus::after {
      box-shadow: 0 0 0 4px var(--website-page__link-color) inset;
    }
  }

  &__meta {
    display: flex;
    flex-direction: row;
    // flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    margin-top: auto;
    padding-top: space(0.5);
    margin-bottom: space(-0.5);
    font-family: var(--website-typography__font-family-body);

    &:empty {
      margin-bottom: 0;
    }
  }

  &__date,
  &__time,
  &__location {
    font-size: 0.85rem !important;
    font-weight: bold;
    font-family: var(--website-typography__font-family-headlines);
    display: flex;
    align-items: flex-start;
    margin-top: space(0.25);
    margin-bottom: space(0.25);
    align-items: center;
    .asd20-icon {
      --line-color: var(--website-icon__line-color);
      margin-left: -0.125em;
      margin-right: space(0.25);
    }
    .date-meta {
      font-size: 0.85rem !important;
    }
  }
  &__time + &__location {
    margin-top: space(0);
    margin-bottom: space(0.25);
  }
  // &__date {
  //   margin-right: space(0.5);
  // }

  &--emphasized-date {
    .asd20-card__header {
      // display: flex;
      align-items: center;
    }
  }

  &__emphasized-date {
    align-self: flex-start;
    position: relative;
    flex-shrink: 0;
    background: var(--website-card__reverse-background-color)
      var(--website-card__reverse-background-style);
    border-radius: var(--website-shape__radius-s);
    box-shadow: none;
    display: flex;
    align-items: center;
    justify-content: center;
    // flex-direction: column;
    // width: 4rem;
    height: 4rem;
    // margin-right: space(0.5);
    margin-bottom: space(0.5);

    small {
      display: none;
      font-size: 0.25rem;
      color: var(--website-icon__fill-color-reverse);
      opacity: 0.75;
    }
    .top {
      margin-right: space(0.5);
      text-align: right;
    }
    .top,
    .bottom {
      color: var(--website-card__reverse-foreground-color);
      text-transform: uppercase;
      // text-align: center;
      font-weight: bold;
      font-size: 0.75rem;
      line-height: 1;
      flex: 0 0 100px;
    }
    .bottom {
      margin-left: space(0.5);
      text-align: left;
    }
    .middle {
      color: var(--website-card__reverse-foreground-color);
      text-transform: uppercase;
      font-weight: bold;
      font-size: 2rem;
      line-height: 1;
      text-align: center;
    }
  }

  &__categories {
    display: flex;
    flex-wrap: wrap;
    margin-left: space(-0.25);
  }

  &__indicators {
    margin-left: auto;
    align-self: flex-end;
    ::v-deep .asd20-icon g {
      --line-color: var(--website-card__reverse-foreground-color);
    }
  }

  &__image {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 0;
    overflow: hidden;
    padding: 0;
    margin: 0;
    background: rgba(0, 0, 0, 0.75);
    transform: translate3d(0, 0, 0);
    -webkit-transform-style: preserve-3d;
    -webkit-perspective: 1000;
    -webkit-backface-visibility: hidden;

    img {
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 1;
      transform: translate3d(0, 0, 0);
      -webkit-transform-style: preserve-3d;
      -webkit-perspective: 1000;
      -webkit-backface-visibility: hidden;

      object-fit: cover;
      object-position: center;
    }
  }

  &--zoom {
    cursor: pointer;
    // transform: scale3d(1, 1, 1);
    &:hover {
      box-shadow: 0 0 0 var(--website-shape__line-thickness)
          var(--website-card__border-color) inset,
        var(--website-card__hover-shadow);
      z-index: 4;
      transform: var(--website-card__hover-transform) !important;
    }
  }

  &__pinned {
    position: absolute;
    top: space(0.5);
    right: space(1);
    --line-color: currentColor;
  }

  &--with-image,
  &--reversed {
    .asd20-card__subtitle {
      color: var(--color__accent);
    }
  }

  &--without-image {
    min-height: 0 !important;
    .asd20-card__content {
      flex-grow: 1;
    }
    .asd20-card__meta {
      margin-top: auto;
    }
  }

  &--seperate-image:not(.asd20-card--without-image) {
    transform: rotate(0deg);
    justify-content: flex-start;
    background: var(--website-card__background-color);
    .asd20-card__image {
      order: -1;
      position: relative;
      top: auto;
      left: auto;
      bottom: auto;
      right: auto;
      padding: 30%;
    }
    .asd20-card__content {
      position: relative;
      flex-grow: 1;
      background: transparent;
    }

    .asd20-card__header {
      position: relative;
      // left: 0;
      // right: 0;
      // bottom: 100%;
      // padding: space(1) space(1);
      // margin-bottom: space(0.5);
      color: var(--website-card__foreground-color);

      // background: linear-gradient(
      //   0deg,
      //   rgba(0, 0, 0, 0.5) 70%,
      //   rgba(0, 0, 0, 0) 100%
      // );
    }

    .asd20-card__date-time,
    .asd20-card__location {
      margin-top: 0;
    }

    .asd20-card__link::after {
      position: fixed;
    }
  }

  &--with-image:not(.asd20-card--seperate-image),
  &--without-image.asd20-card--reversed {
    .asd20-card__title {
      color: var(--website-card__reverse-foreground-color);
    }
    .asd20-card__link {
      // color: var(--website-card__reverse-foreground-color) !important;
    }
  }

  &--with-image:not(.asd20-card--seperate-image) {
    min-height: 30vh;
    .asd20-card__content {
      background: linear-gradient(
        0deg,
        rgba(0, 0, 0, 0.5) 70%,
        rgba(0, 0, 0, 0) 100%
      );
    }
  }

  &--reversed,
  &--with-image:not(.asd20-card--seperate-image) {
    color: var(--website-card__reverse-foreground-color);
    // .asd20-card__date-time,
    // .asd20-card__location {
    //   .asd20-icon {
    //     --line-color: var(--website-icon__fill-color-reverse);
    //   }
    // }
  }
  &--reversed {
    background: var(--website-card__reverse-background-color)
      var(--website-card__reverse-background-style);
    // box-shadow: none;
    &:hover {
      box-shadow: 0 0 0 0 transparent, var(--website-card__hover-shadow);
      background: var(--website-card__hover-reverse-background-color);
    }
    ::v-deep .asd20-icon {
      --line-color: var(--website-card__reverse-foreground-color);
    }
    // .asd20-card__date-time,
    // .asd20-card__location {
    //   &::v-deep .asd20-icon g{
    //     --line-color: var(--website-icon__fill-color-reverse);
    //     // --line-color: red;
    //   }
    // }
  }

  &--expanded-header {
    // background: var(--website-card__hover-background-color);
    .asd20-card__header {
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }
  }
}

@media (min-width: 768px) {
  .asd20-card {
    min-height: 25vw;

    &__description {
      display: block;
    }
  }
}

@media (min-width: 1024px) {
  .asd20-card {
    // font-size: 1.25rem;
    min-height: 15vw;

    &__description {
      display: block;
    }
  }
}
</style>
